import React, { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { MoreVertical } from 'lucide-react';
import Badge from '../UI/Badge';
import Button from '../UI/Button';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '../UI/dropdown-menu';
import { ordersAPI, plannedOrdersAPI } from '../../services/api';

const ACTIVE_STATUSES = ['new', 'accepted', 'pending_new', 'partially_filled'];
const ALL_ORDER_STATUSES = ['new', 'accepted', 'pending_new', 'partially_filled', 'filled', 'canceling', 'canceled', 'expired', 'rejected', 'replaced'];
const ALL_PLANNED_STATUSES = ['pending', 'queued', 'submitted', 'filled', 'canceled', 'expired', 'rejected', 'skipped'];
const ACTIVE_PLANNED_STATUSES = ['pending', 'queued', 'submitted'];
const CANCELABLE_STATUSES = ['new', 'accepted', 'pending_new', 'partially_filled'];

const TIF_LABELS = {
  day: 'Day',
  gtc: 'Good Till Canceled',
  ioc: 'Immediate or Cancel',
  fok: 'Fill or Kill',
  opg: 'Market on Open',
  cls: 'Market on Close',
};

const getOrderTypeBadge = (type) => {
  const typeMap = {
    limit: { variant: 'info', label: 'LIMIT' },
    market: { variant: 'primary', label: 'MARKET' },
    stop: { variant: 'warning', label: 'STOP' },
    stop_limit: { variant: 'warning', label: 'STOP LMT' },
  };
  const config = typeMap[type] || { variant: 'neutral', label: type?.toUpperCase() };
  return <Badge variant={config.variant}>{config.label}</Badge>;
};

const getStatusBadge = (status) => {
  const statusMap = {
    new: 'info',
    accepted: 'info',
    pending_new: 'info',
    partially_filled: 'warning',
    filled: 'success',
    canceling: 'warning',
    canceled: 'danger',
    expired: 'danger',
    rejected: 'danger',
    replaced: 'neutral',
  };
  return <Badge variant={statusMap[status] || 'neutral'}>{status?.replace('_', ' ').toUpperCase()}</Badge>;
};

const MiniSpinner = () => (
  <div className="animate-spin rounded-full h-3.5 w-3.5 border-[1.5px] border-yellow-400 border-t-transparent flex-shrink-0" />
);

const FillProgressBar = ({ filled, total }) => {
  const pct = total > 0 ? Math.min((filled / total) * 100, 100) : 0;
  return (
    <div className="mt-1.5 flex items-center gap-2">
      <div className="flex-1 h-1.5 rounded-full bg-border overflow-hidden">
        <div
          className="h-full rounded-full bg-yellow-400 transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-[10px] text-yellow-400 font-medium whitespace-nowrap">
        {filled.toFixed(2)}/{total.toFixed(2)}
      </span>
    </div>
  );
};

const PLANNED_STATUS_MAP = {
  pending: 'warning',
  queued: 'primary',
  submitted: 'info',
  filled: 'success',
  canceled: 'danger',
  expired: 'danger',
  rejected: 'danger',
  skipped: 'neutral',
};

const STATUS_VARIANT = {
  new: 'info', accepted: 'info', pending_new: 'info', partially_filled: 'warning',
  filled: 'success', canceling: 'warning', canceled: 'danger', expired: 'danger',
  rejected: 'danger', replaced: 'neutral',
  pending: 'warning', queued: 'primary', submitted: 'info', skipped: 'neutral',
};

function FilterPopover({ allStatuses, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (status) => {
    const next = new Set(selected);
    if (next.has(status)) next.delete(status);
    else next.add(status);
    onChange(next);
  };

  const allSelected = allStatuses.every((s) => selected.has(s));
  const noneSelected = selected.size === 0;
  const isFiltered = selected.size !== allStatuses.length;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className={`flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
          isFiltered ? 'text-cyan-400 bg-cyan-400/10' : 'text-muted-foreground hover:text-foreground hover:bg-accent/20'
        }`}
        title="Filter by status"
      >
        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-44 bg-card border border-border rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="px-3 py-2 border-b border-border flex items-center justify-between">
            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Status Filter</span>
            <button
              onClick={() => onChange(new Set(allSelected ? [] : allStatuses))}
              className="text-[10px] text-cyan-400 hover:text-cyan-300"
            >
              {allSelected ? 'None' : 'All'}
            </button>
          </div>
          <div className="py-1">
            {allStatuses.map((status) => (
              <label
                key={status}
                className="flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-accent/10 transition-colors"
              >
                <input
                  type="checkbox"
                  checked={selected.has(status)}
                  onChange={() => toggle(status)}
                  className="rounded accent-cyan-500 w-3 h-3"
                />
                <Badge variant={STATUS_VARIANT[status] || 'neutral'}>
                  {status.replace('_', ' ').toUpperCase()}
                </Badge>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const OrderBook = ({ strategyId, strategy, plannedOrders = [], isMobile = false }) => {
  const [orderFilters, setOrderFilters] = useState(new Set(ACTIVE_STATUSES));
  const [plannedFilters, setPlannedFilters] = useState(new Set(ACTIVE_PLANNED_STATUSES));
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [viewMode, setViewMode] = useState('orders'); // 'orders' or 'planned'
  const queryClient = useQueryClient();

  const { data: ordersData, isLoading } = useQuery({
    queryKey: ['orders', strategyId, 'all'],
    queryFn: () => ordersAPI.getByStrategy(strategyId, 'all'),
    enabled: !!strategyId,
    refetchInterval: 5000,
  });

  const [cancelingIds, setCancelingIds] = useState(new Set());

  const cancelMutation = useMutation({
    mutationFn: async (orderId) => {
      setCancelingIds((prev) => new Set(prev).add(orderId));
      const minDelay = new Promise((r) => setTimeout(r, 5000));
      const [result] = await Promise.all([ordersAPI.cancel(orderId), minDelay]);
      return result;
    },
    onSuccess: (_data, orderId) => {
      setCancelingIds((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['orders', strategyId] });
    },
    onError: (err, orderId) => {
      setCancelingIds((prev) => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      toast.error(`Cancel failed: ${err.message}`);
    },
  });

  const cancelPlannedMutation = useMutation({
    mutationFn: (id) => plannedOrdersAPI.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['plannedOrders', strategyId] });
    },
    onError: (err) => toast.error(`Cancel failed: ${err.message}`),
  });

  // Inject queued planned orders into the orders view as "ready to fire" entries
  const queuedAsOrders = plannedOrders
    .filter((po) => po.status === 'queued')
    .map((po) => ({
      id: `queued-${po.id}`,
      _plannedOrderId: po.id,
      _isQueued: true,
      symbol: po.symbol,
      side: po.side,
      quantity: po.quantity,
      order_type: po.order_type || 'market',
      limit_price: po.limit_price,
      price: po.limit_price,           // target price for display
      status: 'queued',
      grid_level: po.grid_level,
      created_at: po.created_at,
      metadata: po.metadata,
    }));

  const allOrders = [...(ordersData?.data || []), ...queuedAsOrders].sort((a, b) => (a.price || a.limit_price || 0) - (b.price || b.limit_price || 0));
  const filteredOrders = allOrders.filter((o) => orderFilters.has(o.status) || o._isQueued);
  const activeCount = allOrders.filter((o) => ACTIVE_STATUSES.includes(o.status) || o._isQueued).length;
  const filteredPlanned = plannedOrders.filter((po) => plannedFilters.has(po.status));
  const activePlannedCount = plannedOrders.filter((po) => ACTIVE_PLANNED_STATUSES.includes(po.status)).length;

  const effectiveSelectedId =
    selectedOrderId && filteredOrders.some((o) => o.id === selectedOrderId)
      ? selectedOrderId
      : filteredOrders[0]?.id || null;

  const selectedOrder = filteredOrders.find((o) => o.id === effectiveSelectedId);

  return (
    <div className="flex flex-col h-full">
      {/* Header with tab toggle */}
      <div className="flex justify-between items-center mb-3 flex-shrink-0">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('orders')}
            className={`text-sm font-bold px-3 py-1.5 sm:px-2 sm:py-0.5 rounded ${viewMode === 'orders' ? 'text-foreground bg-accent/20' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Orders
          </button>
          <button
            onClick={() => setViewMode('planned')}
            className={`text-sm font-bold px-3 py-1.5 sm:px-2 sm:py-0.5 rounded ${viewMode === 'planned' ? 'text-foreground bg-accent/20' : 'text-muted-foreground hover:text-foreground'}`}
          >
            Planned
          </button>
          <Badge variant="info">
            {viewMode === 'orders' ? `${activeCount} active` : `${activePlannedCount} active`}
          </Badge>
        </div>
        {viewMode === 'orders' ? (
          <FilterPopover allStatuses={ALL_ORDER_STATUSES} selected={orderFilters} onChange={setOrderFilters} />
        ) : (
          <FilterPopover allStatuses={ALL_PLANNED_STATUSES} selected={plannedFilters} onChange={setPlannedFilters} />
        )}
      </div>

      {viewMode === 'orders' ? (
        <>
          {/* Column headers */}
          <div className="grid grid-cols-4 gap-2 text-[10px] text-muted-foreground font-medium px-3 pb-1.5 border-b border-border flex-shrink-0">
            <div>Side/Type</div>
            <div className="text-right">Qty</div>
            <div className="text-right">Price</div>
            <div>Status</div>
          </div>

          {/* Scrollable order list */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {isLoading ? (
              <div className="text-center py-6 text-muted-foreground text-sm">Loading orders...</div>
            ) : filteredOrders.length > 0 ? (
              filteredOrders.map((order) => {
                const isCanceling = cancelingIds.has(order.id);
                return (
                  <div
                    key={order.id}
                    onClick={() => !isMobile && setSelectedOrderId(order.id)}
                    className={`px-3 py-3 sm:py-2 border-l-2 transition-colors ${
                      !isMobile ? 'cursor-pointer' : ''
                    } ${
                      !isMobile && order.id === effectiveSelectedId
                        ? 'bg-primary/10 border-primary'
                        : 'border-transparent hover:bg-accent/10'
                    }`}
                  >
                    {isCanceling ? (
                      <div className="flex items-center gap-2.5 py-0.5">
                        <MiniSpinner />
                        <span className="text-xs text-yellow-400 animate-pulse">
                          Canceling {order.side?.toUpperCase()} {parseFloat(order.quantity).toFixed(2)} @ ${(order.price || order.limit_price)?.toFixed?.(2) || 'MKT'}...
                        </span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <div className="flex-1 min-w-0">
                          <div className="grid grid-cols-4 gap-2 items-center">
                            <div className="flex items-center gap-1.5">
                              <span
                                className={`text-xs font-bold ${order.side === 'buy' ? 'text-green-500' : 'text-red-500'}`}
                              >
                                {order.side?.toUpperCase()}
                              </span>
                              {getOrderTypeBadge(order.type || order.order_type)}
                            </div>
                            <div className="text-xs text-muted-foreground text-right">
                              {parseFloat(order.quantity).toFixed(2)}
                            </div>
                            <div className="text-xs text-muted-foreground text-right">
                              {order._isQueued && <span className="text-[9px] text-primary mr-0.5">target </span>}
                              ${(order.price || order.limit_price) != null ? parseFloat(order.price || order.limit_price).toFixed(2) : '-'}
                              {order.metadata?.priceDelta != null && (
                                <span className={`block text-[9px] ${order.metadata.priceDelta >= 0 ? 'text-red-400' : 'text-green-400'}`}>
                                  {order.metadata.priceDelta >= 0 ? '+' : ''}{order.metadata.priceDelta.toFixed(4)} ({order.metadata.priceDeltaPercent?.toFixed(2)}%)
                                </span>
                              )}
                            </div>
                            <div>{getStatusBadge(order.status)}</div>
                          </div>
                          {order.status === 'partially_filled' && (
                            <FillProgressBar filled={parseFloat(order.filledQty || order.filled_qty || 0)} total={parseFloat(order.quantity)} />
                          )}
                        </div>
                        {isMobile && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                onClick={(e) => e.stopPropagation()}
                                className="flex-shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/20 transition-colors"
                              >
                                <MoreVertical className="h-4 w-4" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="min-w-[180px]">
                              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/60">
                                {order.side?.toUpperCase()} {parseFloat(order.quantity).toFixed(2)} @ ${(order.price || order.limit_price) != null ? parseFloat(order.price || order.limit_price).toFixed(2) : 'MKT'}
                              </DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem disabled className="text-xs opacity-100">
                                <span className="text-muted-foreground/60 mr-1.5">Type</span>
                                {(order.type || order.order_type)?.toUpperCase()}
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled className="text-xs opacity-100">
                                <span className="text-muted-foreground/60 mr-1.5">Status</span>
                                {order.status?.replace('_', ' ').toUpperCase()}
                              </DropdownMenuItem>
                              <DropdownMenuItem disabled className="text-xs opacity-100">
                                <span className="text-muted-foreground/60 mr-1.5">Filled</span>
                                {parseFloat(order.filledQty || order.filled_qty || 0).toFixed(2)} / {parseFloat(order.quantity).toFixed(2)}
                              </DropdownMenuItem>
                              {(CANCELABLE_STATUSES.includes(order.status) || order._isQueued) && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className="text-red-400 focus:text-red-300 text-xs"
                                    onClick={() => order._isQueued
                                      ? cancelPlannedMutation.mutate(order._plannedOrderId)
                                      : cancelMutation.mutate(order.id)
                                    }
                                  >
                                    {order._isQueued ? 'Cancel Queued Order' : 'Cancel Order'}
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">
                No matching orders
              </div>
            )}
          </div>

          {/* Selected order details — desktop only */}
          {!isMobile && (
            <div className="flex-shrink-0 border-t border-border mt-2 pt-3">
              {selectedOrder ? (
                <SelectedOrderDetails
                  order={selectedOrder}
                  isCanceling={cancelingIds.has(selectedOrder.id)}
                  onCancel={() => cancelMutation.mutate(selectedOrder.id)}
                />
              ) : (
                <div className="text-center py-2 text-muted-foreground text-xs">
                  {isLoading ? 'Loading...' : 'No order selected'}
                </div>
              )}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Planned Orders Column headers */}
          <div className="grid grid-cols-4 gap-2 text-[10px] text-muted-foreground font-medium px-3 pb-1.5 border-b border-border flex-shrink-0">
            <div>Side/Type</div>
            <div className="text-right">Qty</div>
            <div className="text-right">Price</div>
            <div>Status</div>
          </div>

          {/* Planned orders list */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {filteredPlanned.length > 0 ? (
              filteredPlanned.map((po) => (
                <div
                  key={po.id}
                  className="px-3 py-3 sm:py-2 border-l-2 border-dashed border-muted-foreground/30 hover:bg-accent/10"
                >
                  <div className="grid grid-cols-4 gap-2 items-center">
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-bold ${po.side === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                        {po.side?.toUpperCase()}
                      </span>
                      {getOrderTypeBadge(po.order_type)}
                    </div>
                    <div className="text-xs text-muted-foreground text-right">
                      {parseFloat(po.quantity).toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground text-right">
                      {po.limit_price != null ? `$${parseFloat(po.limit_price).toFixed(2)}` : '-'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Badge variant={PLANNED_STATUS_MAP[po.status] || 'neutral'}>
                        {po.status?.toUpperCase()}
                      </Badge>
                      {strategy?.status === 'paused' && po.status === 'pending' && (
                        <span className="text-[10px] italic text-yellow-400 whitespace-nowrap">queued for resume</span>
                      )}
                    </div>
                  </div>
                  {po.order_id && (
                    <div className="text-[10px] text-muted-foreground/50 mt-0.5">
                      Linked: {po.order_id.slice(0, 8)}...
                    </div>
                  )}
                  {['pending', 'submitted'].includes(po.status) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="mt-1 text-xs text-red-400 hover:text-red-300 p-0 h-auto"
                      onClick={() => cancelPlannedMutation.mutate(po.id)}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center py-6 text-muted-foreground text-sm">
                No matching planned orders
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

function SelectedOrderDetails({ order, isCanceling, onCancel }) {
  const displayStatus = isCanceling ? 'canceling' : order.status;

  return (
    <div className="space-y-2">
      {/* Title row */}
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-foreground">
          {order.side?.toUpperCase()} {parseFloat(order.quantity).toFixed(2)} {order.symbol}
        </span>
        {isCanceling ? (
          <div className="flex items-center gap-1.5">
            <MiniSpinner />
            {getStatusBadge(displayStatus)}
          </div>
        ) : (
          getStatusBadge(displayStatus)
        )}
      </div>

      {/* Detail grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <DetailField label="Type" value={(order.type || order.order_type)?.toUpperCase()} />
        <DetailField label="Shares" value={parseFloat(order.quantity).toFixed(2)} />
        <DetailField
          label="Limit Price"
          value={(order.price ?? order.limit_price) != null ? `$${parseFloat(order.price ?? order.limit_price).toFixed(2)}` : '-'}
        />
        {(order.stopPrice ?? order.stop_price) != null && (
          <DetailField
            label="Stop Price"
            value={`$${parseFloat(order.stopPrice ?? order.stop_price).toFixed(2)}`}
            className="text-red-400"
          />
        )}
        {(order.filledPrice ?? order.filled_price) != null && (
          <DetailField
            label="Filled Price"
            value={`$${parseFloat(order.filledPrice ?? order.filled_price).toFixed(2)}`}
            className="text-green-400"
          />
        )}
        <DetailField
          label="Filled"
          value={`${parseFloat(order.filledQty || order.filled_qty || 0).toFixed(2)} / ${parseFloat(order.quantity).toFixed(2)}`}
        />
        {order.status === 'partially_filled' && (
          <div className="col-span-2">
            <FillProgressBar filled={parseFloat(order.filledQty || order.filled_qty || 0)} total={parseFloat(order.quantity)} />
          </div>
        )}
        <DetailField
          label="Time in Force"
          value={TIF_LABELS[order.timeInForce || order.time_in_force] || (order.timeInForce || order.time_in_force)?.toUpperCase() || '-'}
        />
        <DetailField
          label="Created"
          value={(order.createdAt || order.created_at) ? new Date(order.createdAt || order.created_at).toLocaleString() : '-'}
        />
        {(order.expiredAt || order.expired_at) && (
          <DetailField
            label="Expired"
            value={new Date(order.expiredAt || order.expired_at).toLocaleString()}
            className="text-red-400"
          />
        )}
        {(order.canceledAt || order.canceled_at) && (
          <DetailField
            label="Canceled"
            value={new Date(order.canceledAt || order.canceled_at).toLocaleString()}
            className="text-red-400"
          />
        )}
        {(order.filledAt || order.filled_at) && (
          <DetailField
            label="Filled At"
            value={new Date(order.filledAt || order.filled_at).toLocaleString()}
            className="text-green-400"
          />
        )}
      </div>

      {/* Cancel button */}
      {CANCELABLE_STATUSES.includes(order.status) && !isCanceling && (
        <Button
          size="sm"
          variant="danger"
          className="w-full mt-1"
          onClick={onCancel}
        >
          Cancel Order
        </Button>
      )}
    </div>
  );
}

function DetailField({ label, value, className = 'text-foreground' }) {
  return (
    <div>
      <span className="text-muted-foreground/60">{label}</span>
      <div className={`font-medium ${className}`}>{value ?? '-'}</div>
    </div>
  );
}

export default OrderBook;
